import * as React from 'react';

import styles from './CatalogCategoryProductsSections.scss';
import { genPublicUrl } from 'libs/image';
import { ProductCard } from 'components/catalogs/components/ProductCard/ProductCard';
import { useCatalogLayoutContext } from 'components/catalogs/layouts/CatalogLayout/CatalogLayout';
import { getCatalog } from 'components/catalogs/libs/catalogApiClient';

type CatalogCategorySummary = import('components/catalogs/libs/catalogApiClient').CatalogCategorySummary;
type CatalogProductCard = import('components/catalogs/libs/catalogApiClient').CatalogProductCard;

type CategoryWithProducts = {
  category: CatalogCategorySummary;
  products: CatalogProductCard[];
};

const toCatalogCategoryPath = (catalogCode: string, categoryCode: string): string =>
  `/catalogs/${encodeURIComponent(catalogCode)}/categories/${encodeURIComponent(categoryCode)}`;

const parseCategoryWithProducts = (data: unknown): CategoryWithProducts[] | null => {
  const rawCategories = (data as { categories?: unknown }).categories;
  if (!Array.isArray(rawCategories)) {
    return null;
  }

  const parsed = rawCategories.map((raw): CategoryWithProducts | null => {
    const rawCategory = (raw as { category?: unknown }).category;
    const rawProducts = (raw as { products?: unknown }).products;
    if (!rawCategory || typeof rawCategory !== 'object' || !Array.isArray(rawProducts)) {
      return null;
    }

    const code = (rawCategory as { code?: unknown }).code;
    const name = (rawCategory as { name?: unknown }).name;
    const imageUrl = (rawCategory as { imageUrl?: unknown }).imageUrl;
    if (typeof code !== 'string' || typeof name !== 'string') {
      return null;
    }

    const products = rawProducts
      .map((p): CatalogProductCard | null => {
        const catalogProductId = (p as { catalogProductId?: unknown }).catalogProductId;
        const title = (p as { title?: unknown }).title;
        const brandsTitle = (p as { brandsTitle?: unknown }).brandsTitle;
        const pImageUrl = (p as { imageUrl?: unknown }).imageUrl;
        if (typeof catalogProductId !== 'number' || typeof title !== 'string') {
          return null;
        }
        return {
          catalogProductId,
          title,
          brandsTitle: typeof brandsTitle === 'string' ? brandsTitle : '',
          imageUrl: typeof pImageUrl === 'string' ? pImageUrl : '',
        };
      })
      .filter((v): v is CatalogProductCard => v !== null);

    return {
      category: { code, name, imageUrl: typeof imageUrl === 'string' ? imageUrl : '' },
      products,
    };
  });

  const sections: CategoryWithProducts[] = [];
  for (const section of parsed) {
    if (section === null) {
      return null;
    }
    sections.push(section);
  }

  return sections;
};

export const CatalogCategoryProductsSections = (): JSX.Element | null => {
  const { catalog, hasError, reportPageError } = useCatalogLayoutContext();
  const catalogCode = catalog?.code ?? '';

  const [sections, setSections] = React.useState<CategoryWithProducts[] | null>(null);
  const [hasCatalogError, setHasCatalogError] = React.useState(false);
  const isActiveRef = React.useRef(true);

  React.useEffect(
    () => () => {
      isActiveRef.current = false;
    },
    []
  );

  const sectionsRef = React.useRef<CategoryWithProducts[] | null>(sections);
  React.useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  const currentCatalogCodeRef = React.useRef(catalogCode);
  React.useEffect(() => {
    currentCatalogCodeRef.current = catalogCode;
  }, [catalogCode]);

  const inFlightRef = React.useRef<Promise<void> | null>(null);
  React.useEffect(() => {
    sectionsRef.current = null;
    setSections(null);
    setHasCatalogError(false);
    inFlightRef.current = null;
  }, [catalogCode]);

  const load = React.useCallback(async (): Promise<void> => {
    if (!catalogCode || hasError) {
      setSections([]);
      return;
    }
    if (sectionsRef.current !== null) {
      return;
    }
    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    const requestCatalogCode = catalogCode;
    const request = (async (): Promise<void> => {
      try {
        if (!isActiveRef.current) {
          return;
        }
        setHasCatalogError(false);
        const response = await getCatalog(requestCatalogCode);
        if (!isActiveRef.current) {
          return;
        }
        if (currentCatalogCodeRef.current !== requestCatalogCode) {
          return;
        }
        const parsed = parseCategoryWithProducts(response);
        if (parsed === null) {
          setHasCatalogError(true);
          reportPageError(new Error('Catalog response shape is invalid.'));
          setSections([]);
          return;
        }
        setSections(parsed);
      } catch (err) {
        if (!isActiveRef.current) {
          return;
        }
        if (currentCatalogCodeRef.current !== requestCatalogCode) {
          return;
        }
        setHasCatalogError(true);
        reportPageError(err);
        setSections([]);
      }
    })();

    inFlightRef.current = request;
    void request.finally(() => {
      if (inFlightRef.current === request) {
        inFlightRef.current = null;
      }
    });

    return request;
  }, [catalogCode, hasError, reportPageError]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (!catalogCode || hasError || hasCatalogError || sections === null || sections.length === 0) {
    return null;
  }

  return (
    <div
      className={styles.wrapper}
      data-testid="catalog-category-products-sections"
    >
      {sections.map(({ category, products }) => (
        <section
          key={category.code}
          className={styles.section}
          aria-label={`${category.name}のカテゴリ商品`}
        >
          <a
            className={styles.categoryLink}
            href={toCatalogCategoryPath(catalogCode, category.code)}
          >
            <div className={styles.categoryImage}>
              {category.imageUrl ? (
                <img
                  className={styles.categoryImageInner}
                  src={genPublicUrl(category.imageUrl, 1920)}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div className={styles.categoryImagePlaceholder} />
              )}
            </div>
            <h2 className={styles.categoryName}>{category.name}</h2>
          </a>

          {products.length > 0 ? (
            <ul className={styles.productsGrid}>
              {products.map((product) => (
                <li
                  key={product.catalogProductId}
                  className={styles.productItem}
                >
                  <ProductCard
                    catalogCode={catalogCode}
                    product={product}
                  />
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ))}
    </div>
  );
};
